import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import useAuth from '@/composables/useAuth';
import { useConnectorTrialEndDate } from '@/composables/useConnectorTrialEndDate';
import {
  CORE_SEAT_COMPONENTS,
  SALES_SEAT_COMPONENTS,
} from '@/constants/billing';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { ADMIN_PANEL, CORE, INTEGRATION, SALES } from '@/constants/team';
import { useBillingStore, useFeatureFlagStore } from '@/stores';
import { useTeamStore } from '@/stores/TeamStore';
import { PricePoint } from '@/types/billing';

import useHasFeature from './useHasFeature';

export default function useSeats() {
  const billingStore = useBillingStore();
  const teamStore = useTeamStore();
  const featureFlagStore = useFeatureFlagStore();

  const { currentAuth } = useAuth();
  const { salesSeats } = storeToRefs(teamStore);
  const {
    isFreeTier,
    isConnectorTier,
    isSupernodeOrEnterpriseTier,
    billingInfo,
    chargifyData,
    hasSubscription,
  } = storeToRefs(billingStore);

  const { isTrueConnector } = useConnectorTrialEndDate();

  const { canGoOverSeatQuota } = useHasFeature();

  const coreSubscriptionSeatCount = computed(
    () => coreSeatComponent.value?.allocated_quantity,
  );
  const coreSeatCount = computed(
    () => billingInfo.value.core?.occupied_seat_count,
  );
  const salesEdgeSeatCount = computed(() => salesSeats.value.length);
  const isCoreSeatEarlyAdopter = computed(() => {
    if (isSupernodeOrEnterpriseTier.value || isFreeTier.value) return false;
    return (
      coreSeatLimit.value > (coreSubscriptionSeatCount.value ?? 0) ||
      coreSeatCount.value > coreSeatLimit.value
    );
  });
  const coreSeatComponent = computed(() =>
    chargifyData.value?.components.find((c) =>
      Object.values(CORE_SEAT_COMPONENTS).includes(c.component_handle),
    ),
  );
  const coreSeatPricePoints = computed(
    () => coreSeatComponent.value?.price_points || [],
  );
  const coreSeatLimit = computed(
    () => billingInfo.value.core?.seat_count ?? Infinity,
  );
  const coreSeatsRemaining = computed(() =>
    Math.max(coreSeatLimit.value - coreSeatCount.value, 0),
  );
  const coreSeatTotalCosts = computed(() =>
    aggregateCosts(coreSeatPricePoints.value),
  );
  const costPerCoreSeat = computed(
    () => coreSeatTotalCosts.value / coreSeatLimit.value,
  );

  const salesSeatComponent = computed(() =>
    chargifyData.value?.components.find((c) =>
      Object.values(SALES_SEAT_COMPONENTS).includes(c.component_handle),
    ),
  );
  const salesSeatPricePoints = computed(
    () => salesSeatComponent.value?.price_points || [],
  );
  const salesSeatCount = computed(
    () => billingInfo.value.sales_seat?.occupied_seat_count || 0,
  );
  const salesSeatLimit = computed(
    () => billingInfo.value.sales_seat?.seat_count || 0,
  );
  const salesSeatsRemaining = computed(() =>
    Math.max(salesSeatLimit.value - salesSeatCount.value, 0),
  );
  const salesSeatTotalCosts = computed(() =>
    aggregateCosts(salesSeatPricePoints.value),
  );
  const costPerSalesSeat = computed(
    () => salesSeatTotalCosts.value / salesSeatLimit.value,
  );

  const integrationSeatCount = computed(
    () => billingInfo.value.integration_seat?.occupied_seat_count ?? 0,
  );
  const integrationSeatLimit = computed(
    () => billingInfo.value.integration_seat?.seat_count ?? null,
  );
  const integrationSeatsRemaining = computed(() => {
    if (integrationSeatLimit.value === null) return Infinity;
    return Math.max(integrationSeatLimit.value - integrationSeatCount.value, 0);
  });
  /* Integration seats cannot currently go over quota (no canGoOverSeatQuota bypass).
     When that changes, remove && !canGoOverSeatQuota.value to match core/sales treatment. */
  const atIntegrationSeatLimit = computed(
    () =>
      integrationSeatLimit.value !== null &&
      integrationSeatsRemaining.value === 0 &&
      !canGoOverSeatQuota.value,
  );
  /* True when the org's tier qualifies it for integration seats at all: Supernode/Enterprise
     qualify unless their quota has been explicitly set to 0, Connector orgs qualify only
     once they've been granted a positive quota. */
  const hasIntegrationSeatTier = computed(
    () =>
      (isSupernodeOrEnterpriseTier.value && integrationSeatLimit.value !== 0) ||
      (isConnectorTier.value && !!integrationSeatLimit.value),
  );
  const canUseIntegrationSeats = computed(
    () =>
      featureFlagStore.hasFeatureFlag(FEATURE_FLAG.TEMP_INTEGRATION_USER) &&
      hasIntegrationSeatTier.value,
  );

  const atCoreSeatLimit = computed(
    () => !coreSeatsRemaining.value && !canGoOverSeatQuota.value,
  );
  const atSalesSeatLimit = computed(
    () => !salesSeatsRemaining.value && !canGoOverSeatQuota.value,
  );
  const atSeatLimit = computed(
    () => atCoreSeatLimit.value && atSalesSeatLimit.value,
  );

  function resolveSeatType(requestedType?: string | null) {
    const atLimit: Record<string, boolean> = {
      [CORE]: atCoreSeatLimit.value,
      [SALES]: atSalesSeatLimit.value,
    };
    const seatType = requestedType ?? CORE;
    if (!atLimit[seatType]) return seatType;
    return seatType === SALES ? CORE : SALES;
  }
  const overSeatLimit = computed(
    () =>
      coreSeatCount.value > coreSeatLimit.value ||
      salesSeatCount.value > salesSeatLimit.value,
  );
  const totalSeatLimit = computed(
    () => coreSeatLimit.value + salesSeatLimit.value,
  );
  const totalSeatCount = computed(
    () => coreSeatCount.value + salesSeatCount.value,
  );

  const hasUnlimitedSeats = computed(
    () => isSupernodeOrEnterpriseTier.value && canGoOverSeatQuota.value,
  );
  const hasPaidSeatLimit = computed(
    () =>
      isConnectorTier.value ||
      (isSupernodeOrEnterpriseTier.value && !canGoOverSeatQuota.value),
  );
  const mustTalkToSales = computed(
    () =>
      isSupernodeOrEnterpriseTier.value ||
      (isTrueConnector.value && !hasSubscription.value),
  );
  const isAdmin = computed(
    () => currentAuth.value?.authorizer_type === ADMIN_PANEL,
  );
  const remainingCoreSeats = computed(
    () => coreSeatsRemaining.value + (isAdmin.value ? 1 : 0),
  );
  const seatsRemainingMap = computed(() => ({
    [CORE]: remainingCoreSeats.value,
    [SALES]: salesSeatsRemaining.value,
    [INTEGRATION]: integrationSeatsRemaining.value,
  }));

  const blockSalesSeatsPPV5 = computed(() => !salesSeatLimit.value);

  /* There will only ever be one price point so far no need to have separate costPerCoreSeat now per price point */
  function aggregateCosts(pricePoints: PricePoint[]) {
    return pricePoints
      .filter(
        (pricePoint) =>
          pricePoint.unit_price_cents > 0 && pricePoint.allocated_quantity > 0,
      )
      .map((pricePoint) => pricePoint.component_price_tier_total_cents)
      .reduce((sum, val) => sum + val, 0);
  }

  return {
    coreSeatCount,
    salesEdgeSeatCount,
    isCoreSeatEarlyAdopter,
    coreSeatPricePoints,
    coreSeatLimit,
    coreSeatsRemaining,
    seatsRemainingMap,
    salesSeatLimit,
    salesSeatCount,
    salesSeatsRemaining,
    salesSeatPricePoints,
    integrationSeatCount,
    integrationSeatLimit,
    integrationSeatsRemaining,
    atIntegrationSeatLimit,
    hasIntegrationSeatTier,
    canUseIntegrationSeats,
    totalSeatLimit,
    totalSeatCount,
    atSeatLimit,
    atCoreSeatLimit,
    atSalesSeatLimit,
    resolveSeatType,
    overSeatLimit,
    costPerCoreSeat,
    coreSeatTotalCosts,
    salesSeatTotalCosts,
    costPerSalesSeat,
    hasPaidSeatLimit,
    mustTalkToSales,
    hasUnlimitedSeats,
    blockSalesSeatsPPV5,
  };
}
